import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { FixedSizeList as List } from 'react-window';
import { CmdCheckbox, CmdLoader } from '@commander-services/gui-components';
import React from 'react';
import { WAYPOINT_WORD_LENGTH } from '../Waypoints/constants';
import { hasBluecoinPosition, searchBluecoin } from './BluecoinService';
import { BluecoinData } from './types';
import {
  bluecoinsArraySelector,
  filteredBluecoinsAtom,
  filteredBluecoinStringAtom,
  isBluecoinDataLoadingAtom,
  isBluecoinListPanelOpenAtom,
  isBluecoinPanelOpenAtom,
  isBluecoinsOpenAtom,
  selectedBluecoinsAtom,
} from './BluecoinState';
import { autofocusAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';
import BluecoinIcon from './BluecoinIcon';

interface BluecoinForm {
  map: google.maps.Map | undefined;
  isLoading: boolean | null;
  isFetched: boolean | null;
}

function BluecoinForm(props: BluecoinForm) {
  const { formatMessage: f } = useIntl();
  const bluecoinsData = useRecoilValue<BluecoinData[]>(bluecoinsArraySelector);
  const [filteredBluecoinsString, setFilteredBluecoinsString] = useRecoilState<string>(
    filteredBluecoinStringAtom
  );
  const [filteredBluecoinsData, setFilteredBluecoinData] =
    useRecoilState<BluecoinData[]>(filteredBluecoinsAtom);
  const isBluecoinsOpenState = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const [isBluecoinPanelOpen, setIsBluecoinPanelOpen] = useRecoilState<number | false>(
    isBluecoinPanelOpenAtom
  );
  const isBluecoinDataLoading = useRecoilValue<boolean>(isBluecoinDataLoadingAtom);
  const [selectedBluecoins, setSelectedBluecoins] = useRecoilState<number[]>(selectedBluecoinsAtom);
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const setIsBluecoinsPanelOpen = useSetRecoilState<number | false>(isBluecoinPanelOpenAtom);
  const [isBluecoinListPanelOpen, setIsBluecoinListPanelOpen] = useRecoilState<number[] | false>(
    isBluecoinListPanelOpenAtom
  );
  const listRef = React.createRef<List>();
  const { trackEvent } = useAnalytics();

  React.useEffect(() => {
    return () => {
      setIsBluecoinsPanelOpen(false);
      setIsBluecoinListPanelOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectBluecoinOnMap = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Select Bluecoin from list']);
    const bluecoinIdData: string | null = event.currentTarget.getAttribute('data-id');
    const mapBounds = new google.maps.LatLngBounds();

    const bluecoin: BluecoinData | undefined = bluecoinsData.find(
      (item: BluecoinData) => item.bluecoinId === Number(bluecoinIdData)
    );

    if (
      props.map &&
      bluecoin &&
      typeof bluecoin.position.latitude === 'number' &&
      typeof bluecoin.position.longitude === 'number'
    ) {
      const position = new google.maps.LatLng(
        bluecoin.position.latitude,
        bluecoin.position.longitude
      );
      selectedBluecoins.forEach((bluecoinId: number): void => {
        const bluecoinItem: BluecoinData | undefined = bluecoinsData.find(
          (item: BluecoinData): boolean => item.bluecoinId === bluecoinId
        );
        if (
          bluecoinItem &&
          typeof bluecoinItem.position.latitude === 'number' &&
          typeof bluecoinItem.position.longitude === 'number'
        ) {
          const bluecoinItemPosition = new google.maps.LatLng(
            bluecoinItem.position.latitude,
            bluecoinItem.position.longitude
          );
          mapBounds.extend(bluecoinItemPosition);
        }
      });

      let closeBluecoinPanel: boolean = false;
      setSelectedBluecoins((oldState: number[]): number[] => {
        if (selectedBluecoins.includes(Number(bluecoinIdData))) {
          if (Number(bluecoinIdData) === isBluecoinPanelOpen) {
            closeBluecoinPanel = true;
          }
          return oldState.filter((item: number): boolean => item !== Number(bluecoinIdData));
        }
        mapBounds.extend(position);
        if (props.map) {
          props.map.fitBounds(mapBounds, { top: 50, right: 50, bottom: 100, left: 300 });
        }
        return [...oldState, Number(bluecoinIdData)];
      });

      if (isBluecoinPanelOpen && selectedBluecoins.length === 1) {
        if (!selectedBluecoins.includes(Number(bluecoinIdData))) {
          setIsBluecoinListPanelOpen((oldState: number[] | false) => {
            if (selectedBluecoins.length === 1 && !oldState && bluecoinIdData) {
              const newState = [...selectedBluecoins, Number(bluecoinIdData)];
              return newState;
            }
            return oldState;
          });
        }
      }

      if (
        isBluecoinPanelOpen === Number(bluecoinIdData) &&
        isBluecoinListPanelOpen &&
        isBluecoinListPanelOpen.includes(Number(bluecoinIdData))
      ) {
        setIsBluecoinPanelOpen(false);
        setIsBluecoinListPanelOpen(false);
      }

      if (
        isBluecoinPanelOpen !== Number(bluecoinIdData) &&
        isBluecoinListPanelOpen &&
        isBluecoinListPanelOpen.includes(Number(bluecoinIdData)) &&
        isBluecoinListPanelOpen.length === 2
      ) {
        setIsBluecoinListPanelOpen(false);
      }

      if (closeBluecoinPanel) {
        setIsBluecoinPanelOpen(false);
      }
    }
  };

  const handleOnChangeSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFilteredBluecoinsString(event.currentTarget.value);
    setAutofocus(true);
  };

  const handleCancelSearch = (): void => {
    setFilteredBluecoinsString('');
    setFilteredBluecoinData([]);
  };

  const renderRow = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }): React.JSX.Element | null => {
    if (bluecoinsData) {
      return (
        <div
          className={`bluecoin-item ${
            selectedBluecoins.includes(bluecoinsData[index].bluecoinId) ? 'active' : ''
          }`}
          data-id={bluecoinsData[index].bluecoinId}
          data-cy={`bluecoin-list-${bluecoinsData[index].bluecoinId}`}
          style={style}
        >
          <label>
            <input
              type="checkbox"
              id={`select-bluecoin-${String(bluecoinsData[index].bluecoinId)}`}
              checked={selectedBluecoins.includes(bluecoinsData[index].bluecoinId)}
              data-id={bluecoinsData[index].bluecoinId}
              onChange={handleSelectBluecoinOnMap}
              disabled={!hasBluecoinPosition(bluecoinsData[index])}
              style={{ width: '16px', height: '16px' }}
            />
            <span className="bluecoin-item-name">
              {bluecoinsData[index] &&
              bluecoinsData[index].name &&
              String(bluecoinsData[index].name).length > WAYPOINT_WORD_LENGTH
                ? `${String(bluecoinsData[index].name).substring(0, WAYPOINT_WORD_LENGTH)} ...`
                : bluecoinsData[index].name}
              {hasBluecoinPosition(bluecoinsData[index]) ? (
                <BluecoinIcon
                  width={16}
                  height={16}
                  bodyColor={bluecoinsData[index].attached ? '#146755' : '#737373'}
                  strokeColor={bluecoinsData[index].attached ? '#05B590' : '#C8C8C8'}
                  title={
                    bluecoinsData[index].attached
                      ? f({ id: 'bluecoin.state.active' })
                      : f({ id: 'bluecoin.state.inactive' })
                  }
                />
              ) : (
                <img
                  src="/images/icons/bluecoin-no-position.svg"
                  width="16"
                  alt={f({ id: 'bluecoins.withoutPosition' })}
                />
              )}
            </span>
            <span className="bluecoin-owner-label">{`${bluecoinsData[index].owner?.label}`}</span>
          </label>
        </div>
      );
    }
    return null;
  };

  const renderSearchRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (filteredBluecoinsData.length > 0 && filteredBluecoinsData[index]) {
      return (
        <div
          className={`bluecoin-item ${
            selectedBluecoins.includes(filteredBluecoinsData[index].bluecoinId) ? 'active' : ''
          }`}
          data-cy={`bluecoin-list-${filteredBluecoinsData[index].bluecoinId}`}
          style={style}
          data-id={bluecoinsData[index].bluecoinId}
        >
          <label>
            <input
              type="checkbox"
              id={`select-bluecoin-${String(filteredBluecoinsData[index].bluecoinId)}`}
              checked={selectedBluecoins.includes(filteredBluecoinsData[index].bluecoinId)}
              data-id={filteredBluecoinsData[index].bluecoinId}
              onChange={handleSelectBluecoinOnMap}
              disabled={!hasBluecoinPosition(filteredBluecoinsData[index])}
              style={{ width: '16px', height: '16px' }}
            />
            <span className="bluecoin-item-name">
              {filteredBluecoinsData[index] &&
              filteredBluecoinsData[index].name &&
              String(filteredBluecoinsData[index].name).length > WAYPOINT_WORD_LENGTH
                ? `${String(filteredBluecoinsData[index].name).substring(0, WAYPOINT_WORD_LENGTH)} ...`
                : filteredBluecoinsData[index].name}
              {hasBluecoinPosition(filteredBluecoinsData[index]) ? (
                <BluecoinIcon
                  width={16}
                  height={16}
                  bodyColor={filteredBluecoinsData[index]?.attached ? '#146755' : '#737373'}
                  strokeColor={filteredBluecoinsData[index]?.attached ? '#05B590' : '#C8C8C8'}
                  title={
                    bluecoinsData[index].attached
                      ? f({ id: 'bluecoin.state.active' })
                      : f({ id: 'bluecoin.state.inactive' })
                  }
                />
              ) : (
                <img
                  src="/images/icons/bluecoin-no-position.svg"
                  alt={f({ id: 'bluecoins.withoutPosition' })}
                  width="16"
                />
              )}
            </span>
            <span className="bluecoin-owner-label">{`${filteredBluecoinsData[index].owner?.label}`}</span>
          </label>
        </div>
      );
    }
    return null;
  };

  React.useEffect(() => {
    if (bluecoinsData) {
      setFilteredBluecoinData(searchBluecoin(bluecoinsData, filteredBluecoinsString));
    }
  }, [bluecoinsData, filteredBluecoinsString, setFilteredBluecoinData]);

  // const handleToggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
  const handleToggleSelectAll = (_value: string): void => {
    // event.preventDefault();
    if (selectedBluecoins.length > 0) {
      setSelectedBluecoins([]);
      setIsBluecoinPanelOpen(false);
      setIsBluecoinListPanelOpen(false);
    }
    if (selectedBluecoins.length === 0) {
      setSelectedBluecoins(
        bluecoinsData
          .filter((bluecoin: BluecoinData) => hasBluecoinPosition(bluecoin))
          .map((bluecoin: BluecoinData) => bluecoin.bluecoinId)
      );
      setAutofocus(true);
    }
  };

  const checkboxValue = (): string => {
    if (selectedBluecoins.length === bluecoinsData.length) {
      return 'checked';
    }
    if (selectedBluecoins.length === 0) {
      return '';
    }
    // if (selectedBluecoins.length < bluecoinsData.length) {
    return 'indeterminate';
    // }
  };

  return (
    <div className="b-mc-waypoints bluecoins-menu">
      {bluecoinsData.length > 0 && (
        <div className="b-mc-waypoints-filter">
          <div className="row align-items-center">
            <div className="col pr-2 mr-2">
              <div className="w-form-control">
                <input
                  type="text"
                  className="form-control"
                  placeholder={f({ id: 'bluecoins.search.placeholder' })}
                  value={filteredBluecoinsString}
                  onChange={handleOnChangeSearch}
                  data-cy="bluecoins-search"
                />
                <i className="cmd-icon cmd-icon-search" />
                {filteredBluecoinsString.length > 0 && (
                  <a
                    onClick={handleCancelSearch}
                    className="cmd-icon cmd-icon-remove remove"
                    data-cy="bluecoin-list-search-clear-icon"
                  >
                    {/* &nbsp; */}
                  </a>
                )}
              </div>
            </div>
            {/* <div className="col-auto pl-0">
              <a
                href="#"
                className={`e-button-sq border-on ${activeFilterBluecoins ? 'bubble-on' : ''}`}
                onClick={props.filterWaypointOpen}
                data-cy="bluecoin-list-filter-icon"
              >
                <i className="cmd-icon cmd-icon-filter" />
              </a>
            </div> */}
          </div>
        </div>
      )}
      {bluecoinsData.length > 0 && (
        <div className="b-mc-waypoints-filter" style={{ padding: '0 8px' }}>
          <CmdCheckbox
            label={`${f({ id: 'bluecoins.selectAll' })} (${selectedBluecoins.length}/${bluecoinsData.length})`}
            name="select-all-bluecoins"
            id="select-all-bluecoins"
            value={checkboxValue()}
            onChangeCallback={handleToggleSelectAll}
          />
        </div>
      )}
      {bluecoinsData.length > 0 && (
        <div className="b-waypoints-list">
          <div className="b-waypoints-list-items">
            {isBluecoinDataLoading && isBluecoinsOpenState && <CmdLoader inContent={true} />}
            {isBluecoinDataLoading ? (
              <a className="item">{f({ id: 'waypoints.loading' })}</a>
            ) : filteredBluecoinsString.length > 0 ? (
              <List
                width={300}
                height={340}
                itemCount={filteredBluecoinsData.length}
                itemSize={35}
                ref={listRef}
              >
                {renderSearchRow}
              </List>
            ) : (
              <List
                width={300}
                height={340}
                itemCount={bluecoinsData.length}
                itemSize={35}
                ref={listRef}
              >
                {renderRow}
              </List>
            )}
          </div>
        </div>
      )}
      {bluecoinsData.length === 0 && props.isLoading && (
        <div className="content-center justify-center flex-1 text-center">
          {f({ id: 'bluecoins.loading' })}
        </div>
      )}
      {bluecoinsData.length === 0 && !props.isLoading && props.isFetched && (
        <div className="content-center justify-center flex-1 text-center">
          {f({ id: 'bluecoins.missing' })}
        </div>
      )}
    </div>
  );
}

export default React.memo(BluecoinForm);
